import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider, HStack, Text, useMediaQuery, VStack } from '@chakra-ui/react';
import MomentLocaleUtils from 'react-day-picker/moment';
import 'moment/locale/de';
import 'react-day-picker/lib/style.css';
import DayPicker, { DateUtils } from 'react-day-picker';
import { getUpdatedObjectFilterDateRangeParams } from '../../../../services/Objects/DateRangeSelectorService';
import { DayPickerContainer } from '../../../common/DayPicker/DayPickerStyled';
import { DayPickerDay } from '../../../common/DayPicker/DayPickerDay';
import { DayPickerNavBar } from '../../../common/DayPicker/DayPickerNavBar';
import { ObjectsParams } from '../../../../types/ParamsTypes';
import { Button } from '@chakra-ui/button';
import moment from 'moment';
import { isQualifiedToUpdateFromAndToDate } from '../../../../services/Date/DateRangeService';
import styled from '@emotion/styled';
import { ModalDateRangeSelector } from './ModalDateRangeSelector';
import { DayPickerRootContainer } from '../../../../styles/DayPickerStyled';

export interface DateRangeSelectorProps {
  objectsParams: ObjectsParams;
  onFilterChange: (objectsParams: ObjectsParams) => void;
  context?: string;
  initialMonth?: Date;
}

export const DateRangeSelector = (props: DateRangeSelectorProps) => {
  const [isTabletOrMobile] = useMediaQuery('(max-width: 767px)');
  const [modalOpen, setModalOpen] = useState<Boolean>(false);
  const [information, setInformation] = useState<string>('');
  const [duration, setDuration] = useState<string>('');
  const { objectsParams, onFilterChange, context, initialMonth } = props;
  const [internalObjectParams, setInternalObjectParams] = useState<ObjectsParams>(objectsParams);
  const { fromDate, toDate } = internalObjectParams;
  const [from, setFrom] = useState<Date | undefined>();
  const [to, setTo] = useState<Date | undefined>();
  const modifiers = { start: from, end: to };

  const handleModalOpen = useCallback(
    (value: Boolean) => {
      setModalOpen(value);
    },
    [setModalOpen],
  );

  const [windowOffset, setWindowOffset] = useState<number>(0);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    const isModalFromAnotherModal = context === 'FILTER_MOBILE';
    if (!isModalFromAnotherModal) {
      document.body.setAttribute('style', '');
      window.scrollTo(0, windowOffset);
    }
  }, [modalOpen, windowOffset]);

  const handleWindowOffset = useCallback((value: number) => {
    setWindowOffset(value);
  }, []);

  const handleDayClick = useCallback(
    (day: Date) => {
      const range = DateUtils.addDayToRange(day, { from, to });

      if (range.from) {
        setFrom(range.from);
      }

      if (range.to) {
        setTo(range.to);
      }
    },
    [setFrom, from, to],
  );

  const handleClose = useCallback(() => {
    closeModal();
    setTimeout(() => {
      if (!from || !to) {
        setFrom(undefined);
        setTo(undefined);
      } else if (
        isQualifiedToUpdateFromAndToDate({
          objectsParamsFrom: fromDate,
          objectsParamsTo: toDate,
          currentFrom: moment(from).format('YYYY-MM-DD'),
          currentTo: moment(to).format('YYYY-MM-DD'),
        })
      ) {
        const result = getUpdatedObjectFilterDateRangeParams(internalObjectParams, { from, to });
        onFilterChange(result);
      }
    }, 200);
  }, [from, to, setModalOpen, objectsParams]);

  const handleCancel = useCallback(() => {
    closeModal();
    if (
      isQualifiedToUpdateFromAndToDate({
        objectsParamsFrom: fromDate,
        objectsParamsTo: toDate,
        currentFrom: moment(from).format('YYYY-MM-DD'),
        currentTo: moment(to).format('YYYY-MM-DD'),
      })
    ) {
      setFrom(undefined);
      setTo(undefined);
      setDuration('');
    }
  }, [from, to, setModalOpen, objectsParams, setDuration, setFrom, setTo]);

  useEffect(() => {
    setFrom(objectsParams.fromDate ? new Date(objectsParams.fromDate) : undefined);
    setTo(objectsParams.toDate ? new Date(objectsParams.toDate) : undefined);
  }, [objectsParams.fromDate, objectsParams.toDate, setFrom, setTo]);

  useEffect(() => {
    if (from && to) {
      setDuration('Reisedauer: ' + moment(to).diff(from, 'days') + ' Tage');
    }
  }, [from, to, setDuration]);

  useEffect(() => {
    let concatInformation = '';

    if (objectsParams.fromDate) {
      concatInformation += new Date(objectsParams.fromDate).toLocaleDateString('de-DE', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    }

    if (objectsParams.fromDate && objectsParams.toDate) {
      concatInformation += ' - ';
    }

    if (objectsParams.toDate) {
      concatInformation += new Date(objectsParams.toDate).toLocaleDateString('de-DE', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    }

    if (concatInformation.length === 0) {
      concatInformation = 'Beliebig';
    }

    setInformation(concatInformation);
  }, [objectsParams.fromDate, objectsParams.toDate]);

  useEffect(() => {
    setInternalObjectParams(objectsParams);
  }, [objectsParams]);

  const stylesForMobileBottomBtns: React.CSSProperties = {
    position: 'fixed',
    bottom: 0,
    zIndex: 2,
    backgroundColor: 'var(--chakra-colors-white)',
    boxShadow: '0 -12px 10px -3px rgba(0, 0, 0, 0.1), 0 0 4px -2px rgba(0, 0, 0, 0.5)',
    padding: '10px 0',
    paddingRight: 'var(--chakra-space-4)',
    right: 0,
    left: 0,
  };

  const DayPickerFull = useMemo(() => {
    return (
      <VStack pb={{ base: 16, md: 0 }}>
        <DayPickerRootContainer>
          <DayPickerContainer>
            <DayPicker
              className={'BookingCalendar'}
              renderDay={(date, modifiers) => <DayPickerDay date={date} modifiers={modifiers} showTooltips={true}/>}
              navbarElement={props => {
                return <DayPickerNavBar {...props} header={<Text>{duration}</Text>} />;
              }}
              month={initialMonth}
              modifiers={modifiers}
              selectedDays={[from, { from, to }]}
              localeUtils={MomentLocaleUtils}
              locale={'de'}
              onDayClick={handleDayClick}
              numberOfMonths={2}
              disabledDays={[{ before: new Date() }]}
            />
          </DayPickerContainer>
        </DayPickerRootContainer>
        {!modalOpen && <Divider />}
        <HStack
          display={modalOpen ? 'flex' : 'none'}
          width={'100%'}
          justifyContent={'flex-end'}
          css={{ '&': { ...(isTabletOrMobile ? stylesForMobileBottomBtns : {}) } }}>
          <Button
            variant={'ghost'}
            onClick={handleCancel}
            colorScheme={'blue'}
            alignSelf={'flex-end'}>
            Abbrechen
          </Button>
          <Button
            disabled={!from || !to}
            onClick={handleClose}
            colorScheme={'blue'}
            alignSelf={'flex-end'}>
            Übernehmen
          </Button>
        </HStack>
      </VStack>
    );
  }, [duration, from, to, modifiers]);

  return (
    <Box>
      <ModalDateRangeSelector
        information={information}
        headline={'Anreise/Abreise'}
        modalOpen={modalOpen}
        handleModalOpen={handleModalOpen}
        windowOffset={windowOffset}
        closeModal={handleCancel}
        handleWindowOffset={handleWindowOffset}
        isTabletOrMobile={isTabletOrMobile}>
        {DayPickerFull}
      </ModalDateRangeSelector>
    </Box>
  );
};
